import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, InfoCircle } from '@saleshandy/icons';
import { Button } from '@saleshandy/design-system';

import Checkbox from '../../../../../../shared/design-system/components/atoms/checkbox';
import { OverlayTooltip } from '../../../../../../shared/design-system/components/overlay';
import InputNumber from '../../../../../../shared/design-system/components/input-number';

type IProps = {
  isVerificationActive: boolean;
  emailVerificationDelayDays: number;
  handleUpdateVerificationSkipInterval: (days: string) => void;
  isLoading: boolean;
};

const SmartReverificationSettings: React.FC<IProps> = ({
  isVerificationActive,
  emailVerificationDelayDays,
  handleUpdateVerificationSkipInterval,
  isLoading,
}) => {
  const [
    isSmartReverificationEnabled,
    setIsSmartReverificationEnabled,
  ] = useState(false);
  const [verificationSkipDays, setVerificationSkipDays] = useState<any>('');
  const [validationError, setValidationError] = useState('');
  const [isSavedBtnClicked, setIsSavedBtnClicked] = useState(false);
  const isDirtyRef = useRef(false);

  const validateDays = (value: string) => {
    if (!isSmartReverificationEnabled) {
      setValidationError('');
      return true;
    }

    const errorMsg =
      'Verification Skip Interval should be between 1 to 1000 days';
    if (!value) {
      setValidationError(errorMsg);
      return false;
    }

    const parsed = Number(value);

    if (parsed < 1 || parsed > 1000) {
      setValidationError(errorMsg);
      return false;
    }

    setValidationError('');
    return true;
  };

  useEffect(() => {
    const hasDays = emailVerificationDelayDays > 0;
    setVerificationSkipDays(
      hasDays ? emailVerificationDelayDays.toString() : '',
    );

    if (hasDays) {
      setIsSmartReverificationEnabled(true);
    } else {
      setIsSmartReverificationEnabled(false);
    }

    isDirtyRef.current = false;
    setIsSavedBtnClicked(false);
  }, [emailVerificationDelayDays]);

  return (
    <div className="smart-reverification__container">
      <Checkbox
        checked={isSmartReverificationEnabled}
        label="Smart Re-Verification During Import"
        onChange={(value) => {
          setIsSmartReverificationEnabled(value);
          if (value) {
            setVerificationSkipDays(
              emailVerificationDelayDays === 0
                ? '15'
                : emailVerificationDelayDays.toString(),
            );
            setValidationError('');
          } else {
            setVerificationSkipDays(0);
            setValidationError('');
          }
          isDirtyRef.current = true;
        }}
        disabled={!isVerificationActive}
      />
      <div
        className={`smart-reverification__content${
          !isVerificationActive || !isSmartReverificationEnabled
            ? ' disabled'
            : ''
        }`}
      >
        <div className="smart-reverification__divider" />

        <div className="smart-reverification__input-field">
          <div className="smart-reverification__input-label-wrapper">
            <p className="smart-reverification__input-label-text">
              Verification Skip Interval
            </p>
            <OverlayTooltip
              className="smart-reverification__tooltip"
              text="Only prospect emails verified within the configured interval will be skipped if detected during import."
            >
              <div className="smart-reverification__info-icon">
                <InfoCircle />
              </div>
            </OverlayTooltip>
          </div>
          <div className="smart-reverification__input-container">
            <InputNumber
              className="smart-reverification__number-input"
              disabled={!isSmartReverificationEnabled}
              value={verificationSkipDays}
              onChange={(num) => {
                setVerificationSkipDays(num);
                validateDays(num);
                isDirtyRef.current = true;
              }}
              min={1}
              max={1000}
            />
            <p className="smart-reverification__input-unit">Days</p>
          </div>
          {validationError ? (
            <div className="smart-reverification__input-error">
              <AlertTriangle />
              <p>{validationError}</p>
            </div>
          ) : null}
        </div>
      </div>

      <Button
        onClick={() => {
          if (validateDays(verificationSkipDays)) {
            handleUpdateVerificationSkipInterval(verificationSkipDays);
            setIsSavedBtnClicked(true);
            isDirtyRef.current = false;
          }
        }}
        disabled={
          !!validationError ||
          (isSavedBtnClicked && isLoading) ||
          !isDirtyRef.current
        }
        isLoading={isSavedBtnClicked && isLoading}
        loadingText="Saving..."
      >
        Save
      </Button>
    </div>
  );
};

export default SmartReverificationSettings;
